import React, { useState } from 'react';
import { Button, Dropdown, Form, Table } from 'react-bootstrap';
import { getApiErrorMessage } from './VotingManagement';
import { getImageUrl } from '../../../utils/imageUrl';
import VotingRemoveModal from './VotingRemoveModal';

export interface VotingPlayer {
  id: string;
  name: string;
  teamName: string;
  profileImage?: string;
}

const VotingProfileImage: React.FC<{ player: VotingPlayer }> = ({ player }) => {
  const [hasImageError, setHasImageError] = useState(false);

  if (player.profileImage && !hasImageError) {
    return (
      <img
        src={getImageUrl(player.profileImage)}
        alt={player.name}
        onError={() => setHasImageError(true)}
        style={{ width: 26, height: 26, borderRadius: '50%', objectFit: 'cover' }}
      />
    );
  }

  return (
    <span aria-label={`${player.name} profile placeholder`} className="d-inline-flex align-items-center justify-content-center bg-light rounded-circle" style={{ width: 26, height: 26 }}>
      <i className="ri-user-line" aria-hidden="true" />
    </span>
  );
};

const getVotingPlayerId = (player?: VotingPlayer | null): string => String(player?.id || '').trim();

const uniqueVotingPlayers = (players: VotingPlayer[]): VotingPlayer[] => {
  const seen = new Set<string>();
  return players.filter(player => {
    const id = getVotingPlayerId(player);
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

interface Props {
  categoryName: string;
  players: VotingPlayer[];
  availablePlayers?: VotingPlayer[];
  playersLoading?: boolean;
  playersError?: string;
  savingPlayers?: boolean;
  onChange: (players: VotingPlayer[]) => void | Promise<void>;
  onBack: () => void;
  onEditCategory: () => void;
  embedded?: boolean;
  itemType?: 'player' | 'team';
  readOnly?: boolean;
}

const VotingCategoryPlayers: React.FC<Props> = ({
  categoryName,
  players,
  availablePlayers = [],
  playersLoading = false,
  playersError = '',
  savingPlayers = false,
  onChange,
  onBack,
  onEditCategory,
  embedded = false,
  itemType = 'player',
  readOnly = false,
}) => {
  const itemLabel = itemType === 'team' ? 'Team' : 'Player';
  const itemLabelLower = itemLabel.toLowerCase();
  const [playerToRemove, setPlayerToRemove] = useState<VotingPlayer | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [error, setError] = useState('');

  const currentPlayers = uniqueVotingPlayers(players);
  const selectablePlayers = uniqueVotingPlayers(availablePlayers).filter(player => !currentPlayers.some(added => getVotingPlayerId(added) === getVotingPlayerId(player)));
  const filteredPlayers = selectablePlayers.filter(player => player.name.toLowerCase().includes(search.trim().toLowerCase()));

  const closeForm = () => {
    setShowForm(false);
    setDropdownOpen(false);
    setSelectedIds([]);
    setSearch('');
    setError('');
  };

  const savePlayers = async (nextPlayers: VotingPlayer[]) => {
    try {
      await onChange(nextPlayers);
      setError('');
      return true;
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to save players.'));
      return false;
    }
  };

  const addPlayers = async (event: React.FormEvent) => {
    event.preventDefault();
    const selectedIdSet = new Set(selectedIds.map(id => String(id).trim()).filter(Boolean));
    const additions = selectablePlayers.filter(player => selectedIdSet.has(getVotingPlayerId(player)));
    if (!additions.length) {
      setError(`Select at least one ${itemLabelLower}.`);
      return;
    }
    const saved = await savePlayers(uniqueVotingPlayers([...currentPlayers, ...additions]));
    if (saved) closeForm();
  };

  const removePlayer = async () => {
    const saved = await savePlayers(currentPlayers.filter(player => getVotingPlayerId(player) !== getVotingPlayerId(playerToRemove)));
    if (saved) setPlayerToRemove(null);
  };

  return (
    <div style={embedded ? { padding: 18, backgroundColor: '#fff', border: '1px solid #e1e5eb', borderRadius: 14, marginTop: 16 } : { padding: '0 5px' }}>
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3">
        <div className="d-flex align-items-center gap-4" style={{ minWidth: 0 }}>
          {!embedded && <Button type="button" variant="light" aria-label={showForm ? 'Back to category players' : 'Back to voting categories'}
            onClick={showForm ? closeForm : onBack}
            className="d-flex align-items-center justify-content-center flex-shrink-0"
            style={{ width: 38, height: 38, borderRadius: 6, boxShadow: '0 1px 5px rgba(0,0,0,0.12)' }}>
            <i className="ri-arrow-left-line" aria-hidden="true" style={{ fontSize: 24, color: '#000' }} />
          </Button>}
          <h4 className="mb-0 fw-bold" style={{ fontSize: embedded ? 16 : 24, color: '#262626', overflowWrap: 'anywhere' }}>{embedded ? 'Players' : categoryName}</h4>
        </div>
        {!showForm && !readOnly && <Button variant={embedded ? 'outline-primary' : 'primary'} onClick={() => setShowForm(true)} style={{ borderRadius: 10, minHeight: embedded ? 36 : 42 }}>{embedded ? `Add ${itemLabel}` : `Add ${itemLabel}s`}</Button>}
      </div>

      {showForm && (
        <Form onSubmit={addPlayers} className="bg-white p-3 mb-3" style={{ border: '1px solid #d9d9d9', borderRadius: 14 }}>
          <h5 className="fw-bold mb-3" style={{ fontSize: 18, color: '#000' }}>Add New {itemLabel}</h5>
          <div style={{ width: 420, maxWidth: '100%' }}>
            <Form.Label id="voting-player-label" style={{ fontSize: 14 }}>Select {itemLabel}<span className="text-danger">*</span></Form.Label>
            <Dropdown show={dropdownOpen} onToggle={setDropdownOpen} autoClose="outside">
              <Dropdown.Toggle id="voting-player-select" variant="light" aria-labelledby="voting-player-label voting-player-select"
                className="w-100 d-flex align-items-center justify-content-between bg-white"
                disabled={playersLoading || savingPlayers}
                style={{ minHeight: 44, border: '1px solid #d9d9d9', borderRadius: 10, color: '#777', padding: '8px 12px', fontSize: 14, fontWeight: 400 }}>
                {selectedIds.length ? `${selectedIds.length} ${itemLabelLower}${selectedIds.length === 1 ? '' : 's'} selected` : playersLoading ? `Loading ${itemLabelLower}s...` : `Select ${itemLabel}`}
              </Dropdown.Toggle>
              <Dropdown.Menu className="w-100 p-3" style={{ border: 0, borderRadius: 12, boxShadow: '0 4px 14px rgba(0,0,0,0.12)', zIndex: 1050, fontSize: 14 }}>
                <div className="border-bottom pb-2 mb-2" style={{ fontSize: 15, fontWeight: 500, color: '#000' }}>Select {itemLabel}s</div>
                <Form.Control type="search" aria-label={`Search ${itemLabel}`} placeholder={`Search ${itemLabel}`} value={search}
                  onChange={event => setSearch(event.target.value)} className="mb-2" style={{ minHeight: 38, borderRadius: 10, fontSize: 14, fontWeight: 400 }} />
                <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                  {playersError && <p className="text-danger mb-2">{playersError}</p>}
                  {filteredPlayers.map(player => (
                    <Form.Check key={getVotingPlayerId(player)} id={`voting-${getVotingPlayerId(player)}`} type="checkbox" label={<span style={{ fontSize: 14, fontWeight: 400 }}>{player.name}</span>}
                      checked={selectedIds.includes(getVotingPlayerId(player))} className="py-1" style={{ fontSize: 14, fontWeight: 400 }}
                      onChange={event => {
                        const playerId = getVotingPlayerId(player);
                        setSelectedIds(previous => event.target.checked
                          ? Array.from(new Set([...previous, playerId]))
                          : previous.filter(id => id !== playerId));
                        setError('');
                      }} />
                  ))}
                  {!playersError && !filteredPlayers.length && <p className="text-muted mb-0">{selectablePlayers.length ? `No ${itemLabelLower}s match your search.` : `All available ${itemLabelLower}s have been added.`}</p>}
                </div>
              </Dropdown.Menu>
            </Dropdown>
            {error && <div role="alert" className="text-danger small mt-2">{error}</div>}
          </div>
          <div className="d-flex justify-content-end gap-3 mt-4">
            <Button type="button" variant="outline-primary" onClick={closeForm} disabled={savingPlayers} style={{ borderRadius: 8, minHeight: 38, padding: '7px 16px', fontSize: 14 }}>Cancel</Button>
            <Button type="submit" disabled={savingPlayers || playersLoading} style={{ borderRadius: 8, minHeight: 38, padding: '7px 16px', fontSize: 14 }}>{savingPlayers ? 'Saving...' : `Add ${itemLabel}`}</Button>
          </div>
        </Form>
      )}

      {currentPlayers.length > 0 ? (
        <div className="bg-white p-3" style={{ border: '1px solid #d9d9d9', borderRadius: 14 }}>
          <Table responsive className="align-middle mb-0">
            <thead><tr>
              {(itemType === 'team' ? ['Profile', 'Team Name', 'Actions'] : ['Profile', 'Player Name', 'Team Name', 'Actions']).map(label => <th key={label} style={{ backgroundColor: '#e5f5ff', padding: '14px 18px', color: '#000' }}>{label}</th>)}
            </tr></thead>
            <tbody>{currentPlayers.map(player => (
              <tr key={getVotingPlayerId(player)}>
                <td style={{ padding: 18 }}>
                  <VotingProfileImage player={player} />
                </td>
                <td style={{ padding: '18px' }}>{player.name}</td>
                {itemType === 'player' && <td style={{ padding: '18px' }}>{player.teamName}</td>}
                <td style={{ padding: '18px' }}>
                  <div className="d-flex gap-2">
                    <Button size="sm" variant="outline-primary" aria-label={`Edit ${player.name}`} title={`Edit ${itemLabelLower}`} onClick={() => {
                      closeForm();
                      onEditCategory();
                    }}><i className="ri-pencil-line" aria-hidden="true" /></Button>
                    {!readOnly && <Button size="sm" variant="outline-danger" aria-label={`Remove ${player.name}`} disabled={savingPlayers} onClick={() => setPlayerToRemove(player)}><i className="ri-delete-bin-line" aria-hidden="true" /></Button>}
                    <Button size="sm" variant="outline-dark" disabled aria-label={`View ${player.name}`} title={`View ${itemLabelLower} (coming soon)`}><i className="ri-eye-line" aria-hidden="true" /></Button>
                  </div>
                </td>
              </tr>
            ))}</tbody>
          </Table>
        </div>
      ) : !showForm && (
        <div className="bg-white d-flex flex-column align-items-center justify-content-center text-center"
          style={{ minHeight: 300, padding: '40px 20px', border: '1px solid #e8ebef', borderRadius: 14, boxShadow: '0 2px 4px rgba(0,0,0,0.06)' }}>
          <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="#6c757d" strokeWidth="2" aria-hidden="true" className="mb-3">
            <circle cx="12" cy="7" r="5" /><path d="M4 23v-1a8 8 0 0 1 16 0v1" />
          </svg>
          <h5 className="fw-bold mb-2" style={{ fontSize: 20, color: '#171717' }}>No {itemLabelLower}s found</h5>
          <p className="mb-0" style={{ fontSize: 16, color: '#7b8493' }}>No {itemLabelLower}s available. Add a new {itemLabelLower} to get started.</p>
        </div>
      )}
      <VotingRemoveModal show={!!playerToRemove} itemType={itemType} onCancel={() => setPlayerToRemove(null)} onConfirm={removePlayer} />
    </div>
  );
};

export default VotingCategoryPlayers;
